/**
 * API Key Service for AIMetaPic V2
 * Handles API key operations and ensures users have API keys
 */

import { authenticatedApiService } from './authenticatedApiService';

interface ApiKeyInfo {
  id: string;
  api_key: string;  // The actual API key (aimetapic_live_...)
  key_name: string;  // Database uses key_name, not name
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
  rate_limit_per_hour: number;
  total_requests: number;
}


interface RegenerateLimit {
  remaining: number;
  used: number;
  max_daily: number;
}

interface GetApiKeysResponse {
  api_keys: ApiKeyInfo[];
  total: number;
  regenerate_limit: RegenerateLimit;
}

class ApiKeyService {
  private static instance: ApiKeyService;

  private constructor() {}

  static getInstance(): ApiKeyService {
    if (!ApiKeyService.instance) {
      ApiKeyService.instance = new ApiKeyService();
    }
    return ApiKeyService.instance;
  }


  /**
   * Get all API keys for the current user
   */
  async getApiKeys(): Promise<{ keys: ApiKeyInfo[]; regenerateLimit: RegenerateLimit | null }> {
    try {
      // [SECURITY] Removed API key logging;
      const response = await authenticatedApiService.get<GetApiKeysResponse>('/api-keys');
      // [SECURITY] Removed API key logging;
      
      if (response.success && response.data) {
        // [SECURITY] Removed API key logging;
        return {
          keys: response.data.api_keys,
          regenerateLimit: response.data.regenerate_limit || null
        };
      }
      
      // [SECURITY] Removed API key logging;
      // [SECURITY] Removed API key logging;
      return { keys: [], regenerateLimit: null };
    } catch (error) {
      // [SECURITY] Removed API key logging;
      return { keys: [], regenerateLimit: null };
    }
  }

  /**
   * Create a new API key (replaces existing one based on backend logic)
   */
  async createApiKey(name: string = 'Default API Key', replaceExisting = true): Promise<{ success: boolean; data?: ApiKeyInfo; error?: string }> {
    try {
      const response = await authenticatedApiService.post<{ api_key: ApiKeyInfo & { key: string } }>('/api-keys', {
        key_name: name,  // Backend expects key_name, not name
        replace_existing: replaceExisting
      });
      
      // [SECURITY] Removed API key logging;

      if (response.success && response.data) {
        // [PRODUCTION] Removed success emoji logging;
        return { success: true, data: response.data.api_key };
      }

      const errorMessage = response.error?.message || 'Failed to create API key';
      console.error('Failed to create API key:', errorMessage);
      return { success: false, error: errorMessage };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error creating API key';
      console.error('Error creating API key:', error);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Regenerate an existing API key (updates the key value)
   */
  async regenerateApiKey(): Promise<{ success: boolean; data?: ApiKeyInfo; error?: string }> {
    try {
      const response = await authenticatedApiService.patch<{ api_key: ApiKeyInfo & { key: string } }>('/api-keys/regenerate');
      
      // [SECURITY] Removed API key logging;

      if (response.success && response.data) {
        // [PRODUCTION] Removed success emoji logging;
        return { success: true, data: response.data.api_key };
      }

      const errorMessage = response.error?.message || 'Failed to regenerate API key';
      console.error('Failed to regenerate API key:', errorMessage);
      return { success: false, error: errorMessage };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error regenerating API key';
      console.error('Error regenerating API key:', error);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Deactivate an API key
   */
  async deactivateApiKey(keyId: string): Promise<boolean> {
    try {
      const response = await authenticatedApiService.delete<{ message: string }>(`/api-keys/${keyId}`);
      
      if (response.success) {
        // [PRODUCTION] Removed success emoji logging;
        return true;
      }

      console.error('Failed to deactivate API key:', response.error);
      return false;
    } catch (error) {
      console.error('Error deactivating API key:', error);
      return false;
    }
  }

  /**
   * Get API key statistics
   */
  async getApiKeyStats(): Promise<any | null> {
    try {
      const response = await authenticatedApiService.get('/api-keys/stats');
      
      if (response.success) {
        return response.data;
      }

      console.error('Failed to fetch API key stats:', response.error);
      return null;
    } catch (error) {
      console.error('Error fetching API key stats:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated before making API calls
   */
  isAuthenticated(): boolean {
    return authenticatedApiService.isAuthenticated();
  }
}

// Export singleton instance
export const apiKeyService = ApiKeyService.getInstance();
export default apiKeyService;
